export interface StreakData {
  current: number;
  longest: number;
  lastActiveDate: string | null;
  startDate?: string;
  milestones?: number[];
}

export interface StreakUpdate {
  maintained: boolean;
  current: number;
  isNew?: boolean;
  broken?: boolean;
  milestone?: boolean;
}

export class StreakManager {
  private STORAGE_KEY = 'flowcus_daily_streak';
  
  /**
   * Get current streak data from localStorage
   */
  getStreakData(): StreakData {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) {
      return {
        current: 0,
        longest: 0,
        lastActiveDate: null,
        milestones: []
      };
    }
    
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error parsing streak data:', error);
      return {
        current: 0,
        longest: 0,
        lastActiveDate: null,
        milestones: []
      };
    }
  }
  
  /**
   * Save streak data to localStorage
   */
  private saveStreakData(data: StreakData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      
      // Dispatch custom event for real-time updates
      window.dispatchEvent(new CustomEvent('streakUpdate', {
        detail: data
      }));
    } catch (error) {
      console.error('Error saving streak data:', error);
    }
  }
  
  /**
   * Check in for today and update streak
   */
  checkIn(): StreakUpdate {
    const data = this.getStreakData();
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    // If already checked in today
    if (data.lastActiveDate === today) {
      return {
        maintained: true,
        current: data.current
      };
    }
    
    let isNew = false;
    let broken = false;
    let milestone = false;
    
    // Check if streak continues or breaks
    if (data.lastActiveDate === yesterday) {
      // Streak continues
      data.current++;
      isNew = true;
    } else if (data.lastActiveDate && data.lastActiveDate !== today) {
      // Streak broken - reset to 1
      if (data.current > 0) {
        broken = true;
      }
      data.current = 1;
      isNew = true;
    } else {
      // First ever check-in or resuming after a break
      data.current = 1;
      data.startDate = data.startDate || today;
      isNew = true;
    }
    
    // Update longest streak
    if (data.current > data.longest) {
      data.longest = data.current;
    }
    
    // Check for milestones
    if (this.isMilestone(data.current) && !data.milestones?.includes(data.current)) {
      milestone = true;
      data.milestones = data.milestones || [];
      data.milestones.push(data.current);
    }
    
    // Update last active date
    data.lastActiveDate = today;
    
    // Save updated data
    this.saveStreakData(data);
    
    return {
      maintained: true,
      current: data.current,
      isNew,
      broken,
      milestone
    };
  }
  
  /**
   * Manually break the streak (for testing or user preference)
   */
  breakStreak(): StreakUpdate {
    const data = this.getStreakData();
    const previousCurrent = data.current;
    
    data.current = 0;
    data.lastActiveDate = null;
    
    this.saveStreakData(data);
    
    return {
      maintained: false,
      current: 0,
      broken: true,
      isNew: previousCurrent > 0
    };
  }
  
  /**
   * Reset all streak data
   */
  reset(): void {
    const resetData: StreakData = {
      current: 0,
      longest: 0,
      lastActiveDate: null,
      milestones: []
    };
    
    this.saveStreakData(resetData);
  }
  
  /**
   * Check if a streak number is a milestone
   */
  private isMilestone(streak: number): boolean {
    const milestones = [3, 7, 14, 30, 60, 100, 200, 365];
    return milestones.includes(streak);
  }
  
  /**
   * Get days since last activity
   */
  getDaysSinceLastActivity(): number | null {
    const data = this.getStreakData();
    if (!data.lastActiveDate) return null;
    
    // Use date strings for comparison to avoid timezone issues
    const lastActiveStr = data.lastActiveDate;
    const todayStr = new Date().toDateString();
    
    if (lastActiveStr === todayStr) {
      return 0; // Same day
    }
    
    // Parse dates and calculate difference
    const lastActive = new Date(lastActiveStr);
    const today = new Date(todayStr);
    
    // Set both to midnight to avoid time-of-day issues
    lastActive.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = today.getTime() - lastActive.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }
  
  /**
   * Check if streak is broken (more than 1 day since last activity)
   */
  isStreakBroken(): boolean {
    const daysSince = this.getDaysSinceLastActivity();
    return daysSince !== null && daysSince > 1;
  }
  
  /**
   * Get streak status message
   */
  getStreakStatus(): {
    status: 'active' | 'broken' | 'new';
    message: string;
    daysSince?: number | null;
  } {
    const data = this.getStreakData();
    const daysSince = this.getDaysSinceLastActivity();
    
    if (!data.lastActiveDate) {
      return {
        status: 'new',
        message: 'Start your first streak today!'
      };
    }
    
    if (daysSince === 0) {
      return {
        status: 'active',
        message: data.current === 1 
          ? 'Great start! Keep it going tomorrow.' 
          : `${data.current} day streak active! 🔥`
      };
    }
    
    if (daysSince === 1) {
      return {
        status: 'active',
        message: 'Check in today to continue your streak!'
      };
    }
    
    return {
      status: 'broken',
      message: `Streak broken ${daysSince} days ago. Start fresh today!`,
      daysSince
    };
  }
  
  /**
   * Get next milestone information
   */
  getNextMilestone(): { days: number; message: string } | null {
    const data = this.getStreakData();
    const allMilestones = [3, 7, 14, 30, 60, 100, 200, 365, 500, 1000];
    
    const nextMilestone = allMilestones.find(milestone => milestone > data.current);
    
    if (!nextMilestone) {
      return null; // Already at the highest milestone
    }
    
    const daysToGo = nextMilestone - data.current;
    
    const milestoneNames: { [key: number]: string } = {
      3: 'Getting Started',
      7: 'One Week Warrior',
      14: 'Two Week Champion',
      30: 'Monthly Master',
      60: 'Two Month Legend',
      100: 'Century Club',
      200: 'Double Century',
      365: 'Year Long Hero',
      500: 'Streak Superstar',
      1000: 'Millennium Master'
    };
    
    return {
      days: daysToGo,
      message: `${daysToGo} days to "${milestoneNames[nextMilestone]}" (${nextMilestone} days)`
    };
  }
  
  /**
   * Get streak statistics for analytics
   */
  getStats(): {
    current: number;
    longest: number;
    totalDays: number;
    averageLength: number;
    milestonesReached: number;
    streaksStarted: number;
  } {
    const data = this.getStreakData();
    
    return {
      current: data.current,
      longest: data.longest,
      totalDays: data.current, // Simplified - could be more complex with historical data
      averageLength: data.longest, // Simplified
      milestonesReached: data.milestones?.length || 0,
      streaksStarted: data.current > 0 ? 1 : 0 // Simplified
    };
  }
}